import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  DocumentTextIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useSubmitExtractionSample, useScopeCategories } from '@/hooks/api';
import type { ExtractionSampleRequest, BuildingClassification, PermitIssuer } from '@/types/api.types';

// Zod validation schema per data-model.md
const extractionSampleSchema = z.object({
  // Required fields
  city: z.string().min(1, 'City is required').max(100),
  county: z.string().min(1, 'County is required').max(100),
  permit_issuer: z.enum(['state', 'local'], {
    error: 'Permit issuer is required',
  }),

  // Optional fields
  project_address: z.string().max(500).optional().or(z.literal('')),
  customer_name: z.string().max(200).optional().or(z.literal('')),
  project_title: z.string().max(200).optional().or(z.literal('')),
  project_total: z.string().max(50).optional().or(z.literal('')),
  num_inspections: z.preprocess(
    (val) => (val === '' || val === undefined ? undefined : Number(val)),
    z.number().min(0).max(100).optional()
  ),
  estimated_fee: z.string().max(50).optional().or(z.literal('')),
  building_classification: z
    .enum(['existing_single_family', 'existing_two_family', 'new_construction', 'commercial', 'other'])
    .optional()
    .or(z.literal('')),
  project_type_select: z.string().optional().or(z.literal('')),
  project_type_custom: z.string().max(200).optional().or(z.literal('')),
  inspector_name: z.string().max(200).optional().or(z.literal('')),
  permit_website: z.string().url('Invalid URL format').optional().or(z.literal('')),
  date_researched: z.string().optional().or(z.literal('')),
  url_status: z.string().max(100).optional().or(z.literal('')),
  notes: z.string().max(5000).optional().or(z.literal('')),
  fee_calc_url: z.string().url('Invalid URL format').optional().or(z.literal('')),
  extraction_steps: z.string().optional().or(z.literal('')),
  fee_discernment: z.string().optional().or(z.literal('')),
  developer_notes: z.string().max(5000).optional().or(z.literal('')),
  test_json: z.string().optional().or(z.literal('')),
  honeypot: z.string().max(100).optional().or(z.literal('')),
});

type FormData = z.infer<typeof extractionSampleSchema>;

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function CollapsibleSection({ title, icon, children, defaultOpen = true }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-purple-600">{icon}</span>
          <span className="font-medium text-gray-900">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDownIcon className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isOpen && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
}

const BUILDING_CLASSIFICATIONS: { value: BuildingClassification; label: string }[] = [
  { value: 'existing_single_family', label: 'Existing Single Family Dwelling' },
  { value: 'existing_two_family', label: 'Existing Two-Family Dwelling (Townhome)' },
  { value: 'new_construction', label: 'New Construction' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'other', label: 'Other' },
];

const PERMIT_ISSUERS: { value: PermitIssuer; label: string }[] = [
  { value: 'state', label: 'State' },
  { value: 'local', label: 'Local' },
];

export default function PermitExtractionInput() {
  const submitMutation = useSubmitExtractionSample();
  const { data: scopeData } = useScopeCategories();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedLocation, setSubmittedLocation] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(extractionSampleSchema) as Resolver<FormData>,
    defaultValues: {
      city: '',
      county: '',
      permit_issuer: undefined,
      project_address: '',
      customer_name: '',
      project_title: '',
      project_total: '',
      num_inspections: undefined,
      estimated_fee: '',
      building_classification: undefined,
      project_type_select: '',
      project_type_custom: '',
      inspector_name: '',
      permit_website: '',
      date_researched: '',
      url_status: '',
      notes: '',
      fee_calc_url: '',
      extraction_steps: '',
      fee_discernment: '',
      developer_notes: '',
      test_json: '',
      honeypot: '',
    },
  });

  const projectTypeSelect = watch('project_type_select');

  const onSubmit = async (data: FormData) => {
    try {
      const project_type =
        data.project_type_select === 'new'
          ? data.project_type_custom || undefined
          : data.project_type_select || undefined;

      // Clean up empty strings to undefined for optional fields
      const cleanedData: ExtractionSampleRequest = {
        city: data.city,
        county: data.county,
        permit_issuer: data.permit_issuer as PermitIssuer,
        project_address: data.project_address || undefined,
        customer_name: data.customer_name || undefined,
        project_title: data.project_title || undefined,
        project_total: data.project_total || undefined,
        num_inspections: data.num_inspections,
        estimated_fee: data.estimated_fee || undefined,
        building_classification: (data.building_classification as BuildingClassification) || undefined,
        project_type,
        inspector_name: data.inspector_name || undefined,
        permit_website: data.permit_website || undefined,
        date_researched: data.date_researched || undefined,
        url_status: data.url_status || undefined,
        notes: data.notes || undefined,
        fee_calc_url: data.fee_calc_url || undefined,
        extraction_steps: data.extraction_steps || undefined,
        fee_discernment: data.fee_discernment || undefined,
        developer_notes: data.developer_notes || undefined,
        test_json: data.test_json || undefined,
        honeypot: data.honeypot || undefined,
      };

      await submitMutation.mutateAsync(cleanedData);

      // Store location for confirmation
      setSubmittedLocation(`${data.city}, ${data.county}`);
      setIsSubmitted(true);

      toast.success(`Extraction sample for ${data.city}, ${data.county} submitted successfully!`);

      // Reset form
      reset();
    } catch (error: unknown) {
      console.error('Extraction sample submission error:', error);
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to submit extraction sample. Please try again.';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Permit Extraction Input</h2>
        <p className="text-gray-600 mt-1">
          Enter permit extraction sample data for a municipality. This data will be sent via email
          to the support team for processing.
        </p>
      </div>

      {isSubmitted && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800 font-semibold mb-1">
            ✓ Extraction sample submitted!
          </p>
          <p className="text-sm text-green-700">
            <strong>Location:</strong> {submittedLocation}
          </p>
          <button
            type="button"
            onClick={() => setIsSubmitted(false)}
            className="mt-2 text-sm text-green-600 hover:text-green-800 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Location Information */}
        <CollapsibleSection
          title="Location Information"
          icon={<MapPinIcon className="w-5 h-5" />}
          defaultOpen={true}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="city"
                {...register('city')}
                className={`input w-full ${errors.city ? 'border-red-500' : ''}`}
                placeholder="e.g., Maple Grove"
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="county" className="block text-sm font-medium text-gray-700 mb-1">
                County <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="county"
                {...register('county')}
                className={`input w-full ${errors.county ? 'border-red-500' : ''}`}
                placeholder="e.g., Hennepin"
              />
              {errors.county && (
                <p className="mt-1 text-sm text-red-600">{errors.county.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="project_address" className="block text-sm font-medium text-gray-700 mb-1">
              Project Address
            </label>
            <textarea
              id="project_address"
              {...register('project_address')}
              rows={2}
              className="input w-full"
              placeholder="Full project address"
            />
          </div>
        </CollapsibleSection>

        {/* Project Details */}
        <CollapsibleSection
          title="Project Details"
          icon={<BuildingOfficeIcon className="w-5 h-5" />}
          defaultOpen={true}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="customer_name" className="block text-sm font-medium text-gray-700 mb-1">
                Customer Name
              </label>
              <input
                type="text"
                id="customer_name"
                {...register('customer_name')}
                className="input w-full"
                placeholder="Customer's full name"
              />
            </div>

            <div>
              <label htmlFor="project_title" className="block text-sm font-medium text-gray-700 mb-1">
                Project Title
              </label>
              <input
                type="text"
                id="project_title"
                {...register('project_title')}
                className="input w-full"
                placeholder="e.g., Indoor Sauna Circuit"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="project_total" className="block text-sm font-medium text-gray-700 mb-1">
                Project Total
              </label>
              <input
                type="text"
                id="project_total"
                {...register('project_total')}
                className="input w-full"
                placeholder="e.g., $895.70"
              />
            </div>

            <div>
              <label htmlFor="num_inspections" className="block text-sm font-medium text-gray-700 mb-1">
                Number of Inspections
              </label>
              <input
                type="number"
                id="num_inspections"
                {...register('num_inspections')}
                min={0}
                max={100}
                className="input w-full"
                placeholder="e.g., 1"
              />
            </div>

            <div>
              <label htmlFor="estimated_fee" className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Permit Fee
              </label>
              <input
                type="text"
                id="estimated_fee"
                {...register('estimated_fee')}
                className="input w-full"
                placeholder="e.g., $79.04"
              />
            </div>
          </div>

          <div>
            <label htmlFor="building_classification" className="block text-sm font-medium text-gray-700 mb-1">
              Building Classification
            </label>
            <select
              id="building_classification"
              {...register('building_classification')}
              className="input w-full"
            >
              <option value="">Select classification...</option>
              {BUILDING_CLASSIFICATIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="project_type_select" className="block text-sm font-medium text-gray-700 mb-1">
              Project Type
            </label>
            <select
              id="project_type_select"
              {...register('project_type_select')}
              className="input w-full"
            >
              <option value="">Select project type...</option>
              {scopeData?.residential && scopeData.residential.length > 0 && (
                <optgroup label="Residential">
                  {scopeData.residential.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {scopeData?.commercial && scopeData.commercial.length > 0 && (
                <optgroup label="Commercial">
                  {scopeData.commercial.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {scopeData?.other && scopeData.other.length > 0 && (
                <optgroup label="Other">
                  {scopeData.other.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </optgroup>
              )}
              <option value="new">— New Project Type —</option>
            </select>
          </div>

          {projectTypeSelect === 'new' && (
            <div>
              <label htmlFor="project_type_custom" className="block text-sm font-medium text-gray-700 mb-1">
                New Project Type
              </label>
              <input
                type="text"
                id="project_type_custom"
                {...register('project_type_custom')}
                className="input w-full"
                placeholder="Enter new project type..."
              />
            </div>
          )}
        </CollapsibleSection>

        {/* Permit Authority */}
        <CollapsibleSection
          title="Permit Authority"
          icon={<ClipboardDocumentListIcon className="w-5 h-5" />}
          defaultOpen={true}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="permit_issuer" className="block text-sm font-medium text-gray-700 mb-1">
                Permit Issuer <span className="text-red-500">*</span>
              </label>
              <select
                id="permit_issuer"
                {...register('permit_issuer')}
                className={`input w-full ${errors.permit_issuer ? 'border-red-500' : ''}`}
              >
                <option value="">Select issuer...</option>
                {PERMIT_ISSUERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.permit_issuer && (
                <p className="mt-1 text-sm text-red-600">{errors.permit_issuer.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="inspector_name" className="block text-sm font-medium text-gray-700 mb-1">
                Inspector Name
              </label>
              <input
                type="text"
                id="inspector_name"
                {...register('inspector_name')}
                className="input w-full"
                placeholder="e.g., Maple Grove Inspections"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="permit_website" className="block text-sm font-medium text-gray-700 mb-1">
                Permit Website
              </label>
              <input
                type="url"
                id="permit_website"
                {...register('permit_website')}
                className={`input w-full ${errors.permit_website ? 'border-red-500' : ''}`}
                placeholder="https://..."
              />
              {errors.permit_website && (
                <p className="mt-1 text-sm text-red-600">{errors.permit_website.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="date_researched" className="block text-sm font-medium text-gray-700 mb-1">
                Date Researched
              </label>
              <input
                type="date"
                id="date_researched"
                {...register('date_researched')}
                className="input w-full"
              />
            </div>
          </div>

          <div>
            <label htmlFor="url_status" className="block text-sm font-medium text-gray-700 mb-1">
              URL Status
            </label>
            <input
              type="text"
              id="url_status"
              {...register('url_status')}
              className="input w-full"
              placeholder="e.g., Active, Broken, Redirects"
            />
          </div>
        </CollapsibleSection>

        {/* Documentation */}
        <CollapsibleSection
          title="Documentation"
          icon={<DocumentTextIcon className="w-5 h-5" />}
          defaultOpen={true}
        >
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              id="notes"
              {...register('notes')}
              rows={3}
              className="input w-full"
              placeholder="General notes about this municipality..."
            />
          </div>

          <div>
            <label htmlFor="fee_calc_url" className="block text-sm font-medium text-gray-700 mb-1">
              Fee Calculation Sheet URL
            </label>
            <input
              type="url"
              id="fee_calc_url"
              {...register('fee_calc_url')}
              className={`input w-full ${errors.fee_calc_url ? 'border-red-500' : ''}`}
              placeholder="https://..."
            />
            {errors.fee_calc_url && (
              <p className="mt-1 text-sm text-red-600">{errors.fee_calc_url.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="extraction_steps" className="block text-sm font-medium text-gray-700 mb-1">
              Permit Fee Extraction Steps
            </label>
            <textarea
              id="extraction_steps"
              {...register('extraction_steps')}
              rows={8}
              className="input w-full font-mono text-sm"
              placeholder="Step-by-step instructions for extracting permit fee information...&#10;&#10;1. Go to jurisdiction lookup...&#10;2. Navigate to permits section...&#10;3. Find fee schedule..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Detailed steps for how to find and extract permit fee information for this municipality.
            </p>
          </div>

          <div>
            <label htmlFor="fee_discernment" className="block text-sm font-medium text-gray-700 mb-1">
              How to Discern Fee
            </label>
            <textarea
              id="fee_discernment"
              {...register('fee_discernment')}
              rows={6}
              className="input w-full font-mono text-sm"
              placeholder="Explanation of how Jason would determine the fee for this municipality..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Notes on how to interpret fee structures and calculate the final permit fee.
            </p>
          </div>

          <div>
            <label htmlFor="developer_notes" className="block text-sm font-medium text-gray-700 mb-1">
              Developer Notes
            </label>
            <textarea
              id="developer_notes"
              {...register('developer_notes')}
              rows={4}
              className="input w-full"
              placeholder="Technical notes for development team..."
            />
          </div>

          <div>
            <label htmlFor="test_json" className="block text-sm font-medium text-gray-700 mb-1">
              Test JSON
            </label>
            <textarea
              id="test_json"
              {...register('test_json')}
              rows={6}
              className="input w-full font-mono text-sm"
              placeholder='{"test": "data"}'
            />
            <p className="mt-1 text-xs text-gray-500">
              Sample JSON data for testing purposes.
            </p>
          </div>
        </CollapsibleSection>

        {/* Honeypot field for spam prevention - hidden from humans */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor="honeypot">Leave this field empty</label>
          <input
            type="text"
            id="honeypot"
            {...register('honeypot')}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={isSubmitting || submitMutation.isPending}
            className="btn btn-primary px-8 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting || submitMutation.isPending ? 'Submitting...' : 'Submit Extraction Sample'}
          </button>
        </div>
      </form>
    </div>
  );
}
